import { useMutation, useApolloClient } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { useMeContext } from '~/modules/me';
import { useProjectResourcePlanPermissions } from '~/modules/resourcing/hooks';
import { dateToMidnightUTCString } from '~/modules/common/dates/convert';
import { useSessionStorage, useIsPtiEnabled } from '~/modules/common/hooks';
import { ResourceRequestStatus } from '~/types';
import {
  TOBEHIRED_RESOURCE_REQUEST,
  invokeMarkResourceRequestAsToBeHired
} from '../enhancers/requestStatusHandlers/withMarkResourceRequestAsToBeHired';
import {
  ALLOCATION_REJECTED_RESOURCE_REQUEST,
  invokeMarkResourceRequestAsAllocationRejected
} from '../enhancers/requestStatusHandlers/withMarkResourceRequestAsAllocationRejected';
import {
  invokeMarkResourceRequestAsComplete,
  getCompleteResourceRequestQueries
} from '../enhancers/requestStatusHandlers/withMarkResourceRequestAsComplete';
import {
  invokeMarkResourceRequestAsDraft,
  UNSUBMIT_RESOURCE_REQUEST
} from '../enhancers/requestStatusHandlers/withMarkResourceRequestAsDraft';
import {
  invokeMarkResourceRequestAsRejected,
  REJECTED_RESOURCE_REQUEST
} from '../enhancers/requestStatusHandlers/withMarkResourceRequestAsRejected';
import {
  invokeMarkResourceRequestAsSubmitted,
  SUBMIT_RESOURCE_REQUEST
} from '../enhancers/requestStatusHandlers/withMarkResourceRequestAsSubmitted';
import {
  invokeMarkResourceRequestAsTentative,
  TENTATIVE_RESOURCE_REQUEST
} from '../enhancers/requestStatusHandlers/withMarkResourceRequestAsTentative';
import { invokeProposeAllocationsForResourceRequest } from '../enhancers/requestStatusHandlers/withBulkProposeAllocationsForResourceRequest';
import {
  updateCache as releaseUpdateCache,
  RELEASE_RESOURCE_ALLOCATIONS
} from '../enhancers/requestStatusHandlers/useReleaseResourceAllocationForRequest';
import { BULK_PROPOSE_ALLOCATIONS_FOR_RESOURCE_REQUEST } from './useBulkProposeAllocationsForResourceRequest';
import { getHasExistingAllocation } from './useGetExistingAllocation';

export const useRequestStatusHandlers = ({
  resourceRequest,
  chartSelectedTab,
  requestAccessLevel,
  chartDisplayDateRange,
  allocationChartRef,
  mergeDialogState = null,
  onMarkResourceRequestAsCompleteAndMerge,
  getCacheUpdateFunctions
}) => {
  const cacheUpdateFunctions = getCacheUpdateFunctions
    ? getCacheUpdateFunctions()
    : {};

  const {
    getDefaultCacheFunctions,
    onResourceRequestUnsubmitCacheFunctions,
    onResourceRequestAcceptCacheFunctions,
    onResourceRequestSubmitCacheFunctions,
    onResourceRequestRejectCacheFunctions,
    onResourceRequestToBeHiredCacheFunctions
  } = cacheUpdateFunctions || {};

  const {
    permissionsMap,
    featureFlags: { isPsaPraaResourceMatchingEnabled }
  } = useMeContext();
  const isPtiEnabled = useIsPtiEnabled();
  const apolloClient = useApolloClient();

  const { storedValue: resourceRequestsQueryVariables } = useSessionStorage(
    'RESOURCE-REQUESTS-QUERY-VARIABLES',
    null
  );

  const {
    isViewAdvancedSettingsEnabled,
    isViewAvailabilitySettingsEnabled
  } = useProjectResourcePlanPermissions(permissionsMap);

  const [markResourceRequestAsToBeHired] = useMutation(
    TOBEHIRED_RESOURCE_REQUEST
  );

  const onResourceRequestToBeHired = useCallback(
    () =>
      invokeMarkResourceRequestAsToBeHired(markResourceRequestAsToBeHired)({
        request: resourceRequest,
        chartSelectedTab,
        requestAccessLevel,
        cacheUpdateFunctions: onResourceRequestToBeHiredCacheFunctions || []
      }),
    [
      markResourceRequestAsToBeHired,
      resourceRequest,
      chartSelectedTab,
      requestAccessLevel,
      onResourceRequestToBeHiredCacheFunctions
    ]
  );

  const showTimeOff =
    isViewAvailabilitySettingsEnabled && Boolean(chartDisplayDateRange);

  const showHolidays =
    isViewAdvancedSettingsEnabled && Boolean(chartDisplayDateRange);

  const formattedDateRange = useMemo(
    () =>
      chartDisplayDateRange
        ? {
            startDate: dateToMidnightUTCString(chartDisplayDateRange.startDate),
            endDate: dateToMidnightUTCString(chartDisplayDateRange.endDate)
          }
        : null,
    [chartDisplayDateRange]
  );

  const [releaseResourceAllocationsForRequest] = useMutation(
    RELEASE_RESOURCE_ALLOCATIONS
  );

  const onReleaseResourceAllocationsForRequest = useCallback(
    ({ resourceAllocations, releaseDate, releaseType, comment }) =>
      resourceRequest &&
      releaseResourceAllocationsForRequest({
        variables: {
          input: {
            releaseType,
            allocationIds: (resourceAllocations || []).map(alloc => alloc.id),
            releaseDate,
            comment,
            sendNotification: true
          },
          chartDateRange: formattedDateRange,
          showTimeOff: Boolean(showTimeOff),
          showHolidays: Boolean(showHolidays)
        },
        update: releaseUpdateCache({
          resourceRequestId: resourceRequest.id,
          resourceRequestsQueryVariables
        })
      }),
    [
      resourceRequest,
      releaseResourceAllocationsForRequest,
      formattedDateRange,
      showTimeOff,
      showHolidays,
      resourceRequestsQueryVariables
    ]
  );

  const [markResourceRequestAsSubmitted] = useMutation(SUBMIT_RESOURCE_REQUEST);

  const onResourceRequestSubmit = useCallback(
    () =>
      invokeMarkResourceRequestAsSubmitted(markResourceRequestAsSubmitted)({
        request: resourceRequest,
        chartSelectedTab,
        requestAccessLevel,
        cacheUpdateFunctions: onResourceRequestSubmitCacheFunctions || [],
        isPtiEnabled,
        isPsaPraaResourceMatchingEnabled
      }),
    [
      chartSelectedTab,
      isPtiEnabled,
      markResourceRequestAsSubmitted,
      requestAccessLevel,
      resourceRequest,
      onResourceRequestSubmitCacheFunctions,
      isPsaPraaResourceMatchingEnabled
    ]
  );

  const onMarkCompleteError = useCallback(
    error => {
      if (
        error?.graphQLErrors?.[0]?.extensions?.code ===
        'MARK_REQUEST_COMPLETE_STATUS_ERROR'
      ) {
        allocationChartRef?.current?.showResourceRequestAcceptErrorDialog?.();
      }
    },
    [allocationChartRef]
  );

  const {
    mutationDocument,
    mutationWithTimeoffDocument
  } = getCompleteResourceRequestQueries();

  const [markResourceRequestAsComplete] = useMutation(mutationDocument, {
    onError: onMarkCompleteError
  });

  const [markResourceRequestAsCompleteWithTimeoff] = useMutation(
    mutationWithTimeoffDocument,
    {
      onError: onMarkCompleteError
    }
  );

  const onResourceRequestAccept = useCallback(() => {
    const markResourceRequestCompleteParams = {
      request: resourceRequest,
      chartSelectedTab,
      requestAccessLevel,
      showTimeOff,
      chartDateRange: formattedDateRange,
      showHolidays,
      filter:
        resourceRequestsQueryVariables && resourceRequestsQueryVariables.filter,
      sort:
        resourceRequestsQueryVariables && resourceRequestsQueryVariables.sort,
      allocationChartRef,
      cacheUpdateFunctions: onResourceRequestAcceptCacheFunctions || []
    };

    if (!showTimeOff && !showHolidays) {
      invokeMarkResourceRequestAsComplete(markResourceRequestAsComplete)(
        markResourceRequestCompleteParams
      );
    } else {
      invokeMarkResourceRequestAsComplete(
        markResourceRequestAsCompleteWithTimeoff
      )(markResourceRequestCompleteParams);
    }
  }, [
    resourceRequest,
    chartSelectedTab,
    requestAccessLevel,
    showTimeOff,
    formattedDateRange,
    showHolidays,
    resourceRequestsQueryVariables,
    allocationChartRef,
    markResourceRequestAsComplete,
    markResourceRequestAsCompleteWithTimeoff,
    onResourceRequestAcceptCacheFunctions
  ]);

  const [markResourceRequestAsRejected] = useMutation(
    REJECTED_RESOURCE_REQUEST
  );

  const onResourceRequestReject = useCallback(
    () =>
      invokeMarkResourceRequestAsRejected(markResourceRequestAsRejected)({
        request: resourceRequest,
        chartSelectedTab,
        requestAccessLevel,
        cacheUpdateFunctions: onResourceRequestRejectCacheFunctions || []
      }),
    [
      markResourceRequestAsRejected,
      resourceRequest,
      chartSelectedTab,
      requestAccessLevel,
      onResourceRequestRejectCacheFunctions
    ]
  );

  const [markResourceRequestAsAllocationRejected] = useMutation(
    ALLOCATION_REJECTED_RESOURCE_REQUEST
  );

  const onResourceRequestAllocationReject = useCallback(
    () =>
      invokeMarkResourceRequestAsAllocationRejected(
        markResourceRequestAsAllocationRejected
      )({
        request: resourceRequest,
        chartSelectedTab,
        requestAccessLevel,
        cacheUpdateFunctions: getDefaultCacheFunctions
          ? getDefaultCacheFunctions({
              responseType: 'markResourceRequestAsAllocationRejected2',
              requestStatus: ResourceRequestStatus.Allocationrejected,
              resourceRequest
            })
          : []
      }),
    [
      markResourceRequestAsAllocationRejected,
      resourceRequest,
      chartSelectedTab,
      getDefaultCacheFunctions,
      requestAccessLevel
    ]
  );

  const [markResourceRequestAsTentative] = useMutation(
    TENTATIVE_RESOURCE_REQUEST
  );

  const onResourceRequestFulfilled = useCallback(
    () =>
      invokeMarkResourceRequestAsTentative(markResourceRequestAsTentative)({
        request: resourceRequest,
        chartSelectedTab,
        requestAccessLevel,
        cacheUpdateFunctions: getDefaultCacheFunctions
          ? getDefaultCacheFunctions({
              responseType: 'markResourceRequestAsTentative2',
              requestStatus: ResourceRequestStatus.Tentative,
              resourceRequest
            })
          : []
      }),
    [
      markResourceRequestAsTentative,
      resourceRequest,
      chartSelectedTab,
      requestAccessLevel,
      getDefaultCacheFunctions
    ]
  );

  const [markResourceRequestAsDraft] = useMutation(UNSUBMIT_RESOURCE_REQUEST);

  const onResourceRequestUnsubmit = useCallback(
    () =>
      invokeMarkResourceRequestAsDraft(markResourceRequestAsDraft)({
        request: resourceRequest,
        requestAccessLevel,
        cacheUpdateFunctions: onResourceRequestUnsubmitCacheFunctions || [],
        isPtiEnabled
      }),
    [
      isPtiEnabled,
      markResourceRequestAsDraft,
      resourceRequest,
      requestAccessLevel,
      onResourceRequestUnsubmitCacheFunctions
    ]
  );

  const onResourceRequestMerge = useCallback(async () => {
    const { hasExistingAllocation, isLoading } = await getHasExistingAllocation(
      {
        apolloClient,
        projectUri: resourceRequest?.projectUri,
        userUris: (resourceRequest?.resourceAllocations || []).map(
          allocation => allocation.user.userUri
        )
      }
    );

    mergeDialogState?.setExistingAllocationState({
      hasExistingAllocation,
      hasExistingAllocationLoading: isLoading
    });

    if (hasExistingAllocation) {
      mergeDialogState?.openDialog();
    } else {
      onMarkResourceRequestAsCompleteAndMerge();
    }
  }, [
    apolloClient,
    mergeDialogState,
    onMarkResourceRequestAsCompleteAndMerge,
    resourceRequest?.projectUri,
    resourceRequest?.resourceAllocations
  ]);

  const [bulkProposeAllocationsForResourceRequest] = useMutation(
    BULK_PROPOSE_ALLOCATIONS_FOR_RESOURCE_REQUEST
  );

  const onResourceRequestProposeTopMatch = useCallback(() => {
    if (!isPsaPraaResourceMatchingEnabled) return;

    const { id, resourceMatches } = resourceRequest;
    const [resourceMatch] = resourceMatches || [];

    invokeProposeAllocationsForResourceRequest(
      bulkProposeAllocationsForResourceRequest
    )([
      {
        resourceRequestId: id,
        userIds: resourceMatch ? [resourceMatch.userUri] : []
      }
    ]);
  }, [
    resourceRequest,
    isPsaPraaResourceMatchingEnabled,
    bulkProposeAllocationsForResourceRequest
  ]);

  return {
    onResourceRequestToBeHired,
    onResourceRequestSubmit,
    onResourceRequestAccept,
    onResourceRequestReject,
    onResourceRequestAllocationReject,
    onResourceRequestFulfilled,
    onResourceRequestUnsubmit,
    onReleaseResourceAllocationsForRequest,
    onResourceRequestMerge,
    onResourceRequestProposeTopMatch
  };
};

export default useRequestStatusHandlers;
